import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { createLogger } from "@/lib/logger";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import type { Tables } from "@/integrations/supabase/types";

const logger = createLogger("EditFlowDialog");

// Schema de validação do formulário
const formSchema = z.object({
  name: z.string().min(3, "Nome deve ter no mínimo 3 caracteres"),
  description: z.string().optional(),
  status: z.enum(["draft", "active", "paused", "archived"]),
});

type FormValues = z.infer<typeof formSchema>;

interface EditFlowDialogProps {
  flow: Tables<"flows"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog para editar propriedades básicas de um fluxo (nome, descrição, status)
 */
export function EditFlowDialog({ flow, open, onOpenChange }: EditFlowDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: flow
      ? {
          name: flow.name,
          description: flow.description || "",
          status: flow.status,
        }
      : undefined,
  });

  // Mutation para atualizar o fluxo
  const updateMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!flow) throw new Error("Fluxo não encontrado");

      // Trava: não permite ativar fluxo sem pelo menos 1 gatilho configurado.
      // Espelha o trigger DB `trg_validate_flow_active_requires_trigger` e
      // antecipa o erro com uma mensagem amigável.
      if (values.status === "active") {
        const triggers = Array.isArray(flow.trigger_config) ? flow.trigger_config : [];
        if (triggers.length === 0) {
          throw new Error(
            "Configure ao menos 1 gatilho antes de ativar o fluxo. Abra as configurações do fluxo (engrenagem) e adicione um gatilho."
          );
        }
      }

      logger.info("Atualizando fluxo", { flowId: flow.id, values });

      const { data, error } = await supabase
        .from("flows")
        .update({
          name: values.name,
          description: values.description || null,
          status: values.status,
          updated_at: new Date().toISOString(),
        })
        .eq("id", flow.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    onSuccess: () => {
      toast({
        title: "Fluxo atualizado",
        description: "As alterações foram salvas com sucesso.",
      });
      queryClient.invalidateQueries({ queryKey: ["flows"] });
      onOpenChange(false);
      form.reset();
    },
    onError: (error: any) => {
      logger.error("Erro ao atualizar fluxo", error);
      // Extrai mensagem amigável (validação local, trigger DB ou erro genérico)
      const rawMessage =
        error?.message || error?.error_description || error?.details || "";
      const isTriggerValidation =
        rawMessage.includes("configure ao menos 1 gatilho") ||
        rawMessage.includes("Configure ao menos 1 gatilho");
      toast({
        variant: "destructive",
        title: isTriggerValidation ? "Gatilho obrigatório" : "Erro ao atualizar",
        description: rawMessage || "Não foi possível salvar as alterações.",
      });
    },

  });

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      await updateMutation.mutateAsync(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Configurações do Fluxo</DialogTitle>
          <DialogDescription>
            Edite as propriedades básicas do fluxo.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Fluxo</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o nome do fluxo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva o objetivo deste fluxo (opcional)"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="draft">Rascunho</SelectItem>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="paused">Pausado</SelectItem>
                      <SelectItem value="archived">Arquivado</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
